/**
 * Hub agencies, teams, and module access.
 *
 * Delegated administration, ported from the Account Inspector: AAS enables an
 * agency and installs its first admin; that admin adds their own people. AAS
 * keeps full visibility. Nobody at AAS maintains a list of every individual.
 *
 * Reads go straight to Supabase under RLS. Writes that create a user go through
 * the backend, because that needs the service-role key.
 */
import type { HubRole } from '@commpass/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { supabase } from '../lib/supabase';

export interface HubAgency {
  member_id: string;
  name: string;
  sf_active: boolean;
  blocked: boolean;
  blocked_reason: string | null;
  /** Both switches must agree before anyone at this agency gets in. */
  enabled: boolean;
}

export interface HubTeamMember {
  user_id: string;
  member_id: string;
  email: string | null;
  full_name: string | null;
  role: HubRole;
  /** Explicit module decisions only — absence means the module's default applies. */
  decisions: Record<string, boolean>;
}

/** Agencies enabled for the hub, with their names from the Salesforce-synced directory. */
export function useHubAgencies() {
  return useQuery<HubAgency[]>({
    queryKey: ['hub-agencies'],
    queryFn: async () => {
      const [rows, names] = await Promise.all([
        supabase.from('hub_agencies').select('member_id, sf_active, blocked, blocked_reason'),
        api.listMembers(),
      ]);
      if (rows.error) throw rows.error;
      const byId = new Map(names.members.map((m) => [m.member_id, m.name]));
      return (rows.data ?? [])
        .map((r) => ({
          ...r,
          name: byId.get(r.member_id) ?? r.member_id,
          enabled: r.sf_active && !r.blocked,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)) as HubAgency[];
    },
  });
}

/** Everyone at the agencies the caller can see. RLS decides which those are. */
export function useHubTeam() {
  return useQuery<HubTeamMember[]>({
    queryKey: ['hub-team'],
    queryFn: async () => {
      const [mem, acc] = await Promise.all([
        supabase.from('hub_memberships').select('user_id, member_id, email, full_name, role'),
        supabase.from('hub_module_access').select('user_id, module_id, granted'),
      ]);
      if (mem.error) throw mem.error;
      if (acc.error) throw acc.error;

      const byUser = new Map<string, Record<string, boolean>>();
      for (const a of acc.data ?? []) {
        const cur = byUser.get(a.user_id) ?? {};
        cur[a.module_id] = a.granted;
        byUser.set(a.user_id, cur);
      }
      return (mem.data ?? []).map((m) => ({
        ...m,
        decisions: byUser.get(m.user_id) ?? {},
      })) as HubTeamMember[];
    },
  });
}

/** AAS only: let an agency into the hub and install its first admin. */
export function useProvisionAgency() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { memberId: string; adminEmail: string; adminName?: string }) =>
      api.hubProvisionAgency(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hub-agencies'] });
      qc.invalidateQueries({ queryKey: ['hub-team'] });
    },
  });
}

/** AAS or the agency's own admin: add someone to a team. */
export function useAddTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { memberId: string; email: string; fullName?: string; role?: HubRole }) =>
      api.hubAddTeamMember(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hub-team'] }),
  });
}

/**
 * A fresh one-time sign-in link.
 *
 * Not a convenience: invitation emails to member agencies are routinely
 * quarantined by Microsoft 365 without ever reaching a mailbox, and most member
 * agencies have no IT department to fix it. Handing the link over by phone or
 * text is the primary path, not the fallback.
 */
export function useSignInLink() {
  return useMutation({
    mutationFn: (email: string) => api.hubSignInLink({ email }),
  });
}

/** Record an explicit module decision for one person. */
export function useSetModuleAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId,
      moduleId,
      granted,
    }: {
      userId: string;
      moduleId: string;
      granted: boolean;
    }) => {
      const email = (await supabase.auth.getUser()).data.user?.email ?? null;
      const { error } = await supabase.from('hub_module_access').upsert({
        user_id: userId,
        module_id: moduleId,
        granted,
        granted_by: email,
        granted_at: new Date().toISOString(),
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hub-team'] }),
  });
}

/** AAS only: block or unblock an agency, independent of the nightly sync. */
export function useSetAgencyBlocked() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ memberId, blocked, reason }: { memberId: string; blocked: boolean; reason?: string }) => {
      const { error } = await supabase
        .from('hub_agencies')
        .update({ blocked, blocked_reason: blocked ? (reason ?? null) : null, updated_at: new Date().toISOString() })
        .eq('member_id', memberId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hub-agencies'] }),
  });
}

export interface AgencyRow {
  member_id: string;
  name: string;
}

/** The Salesforce-synced agency directory, same source the wizard's picker uses. */
export function useMembers() {
  return useQuery<AgencyRow[]>({
    queryKey: ['members'],
    queryFn: async () => (await api.listMembers()).members,
    staleTime: 5 * 60 * 1000,
  });
}
